
import { Options, Vue } from 'vue-class-component';
import { SurveyTemplateClient } from '@/services/DueDiligenceServices';
import * as DDM from '@/dueDiligenceModels';
import InfoModal from '@/components/modals/infoModal.vue';

@Options({})
export default class SurveyTemplateEditor extends Vue {

    template: DDM.SurveyTemplate = new DDM.SurveyTemplate();
    loaded: boolean = false;
    saving: boolean = false;
    expandedSections: boolean[] = [];

    created() {
        SurveyTemplateClient.getDefault()
        .then(x => {
            if (x) {
                this.template = x;
                this.ensureLocalizedStrings();
            }
            this.expandedSections = this.template.sections.map(() => false);
            this.loaded = true;
        })
        .catch(() => {
            // No template exists yet - start with empty
            this.loaded = true;
        });
    }

    get totalWeight(): number {
        return this.template.sections.reduce((sum, s) => sum + (s.weight || 0), 0);
    }

    toggleSection(idx: number) {
        this.expandedSections[idx] = !this.expandedSections[idx];
    }

    getSectionTitle(section: DDM.SurveySection): string {
        return section.title?.values?.['en-US'] || section.title?.values?.['it-IT'] || section.sectionId;
    }

    ensureLocalizedStrings() {
        for (const section of this.template.sections) {
            if (!section.title?.values) section.title = new DDM.LocalizedString();
            if (!section.title.values['en-US']) section.title.values['en-US'] = '';
            if (!section.title.values['it-IT']) section.title.values['it-IT'] = '';
            if (!section.description?.values) section.description = new DDM.LocalizedString();

            for (const q of section.questions) {
                if (!q.text?.values) q.text = new DDM.LocalizedString();
                if (!q.text.values['en-US']) q.text.values['en-US'] = '';
                if (!q.text.values['it-IT']) q.text.values['it-IT'] = '';
                if (q.allowFileUpload === undefined) q.allowFileUpload = false;
                if (q.isCertificateDocument === undefined) q.isCertificateDocument = false;

                if (q.subQuestions) {
                    for (const sub of q.subQuestions) {
                        if (!sub.text?.values) sub.text = new DDM.LocalizedString();
                        if (!sub.text.values['en-US']) sub.text.values['en-US'] = '';
                        if (!sub.text.values['it-IT']) sub.text.values['it-IT'] = '';
                        if (sub.allowFileUpload === undefined) sub.allowFileUpload = false;
                        if (sub.isCertificateDocument === undefined) sub.isCertificateDocument = false;
                        if (sub.scoreGroup === undefined) sub.scoreGroup = "";
                        if (sub.scoreGroupRank === undefined) sub.scoreGroupRank = 0;
                    }
                }
            }
        }
    }

    addSection() {
        var newSection = new DDM.SurveySection();
        newSection.sectionId = 'S' + (this.template.sections.length + 2);
        newSection.sortOrder = this.template.sections.length + 1;
        newSection.title.values['en-US'] = '';
        newSection.title.values['it-IT'] = '';
        this.template.sections.push(newSection);
        this.expandedSections.push(true);
    }

    removeSection(idx: number) {
        if (confirm('Remove this section?')) {
            this.template.sections.splice(idx, 1);
            this.expandedSections.splice(idx, 1);
        }
    }

    addQuestion(section: DDM.SurveySection) {
        var newQ = new DDM.SurveyQuestion();
        var maxSort = section.questions.reduce((max, q) => Math.max(max, q.sortOrder), 0);
        newQ.questionId = section.sectionId + '_Q' + (section.questions.length + 1);
        newQ.sortOrder = maxSort + 1;
        newQ.text.values['en-US'] = '';
        newQ.text.values['it-IT'] = '';
        section.questions.push(newQ);
    }

    removeQuestion(section: DDM.SurveySection, idx: number) {
        if (confirm('Remove this question?')) {
            section.questions.splice(idx, 1);
        }
    }

    addSubQuestion(question: DDM.SurveyQuestion) {
        if (!question.subQuestions) question.subQuestions = [];
        var sub = new DDM.SurveySubQuestion();
        sub.subQuestionId = question.questionId + '_' + (question.subQuestions.length + 1);
        sub.text.values['en-US'] = '';
        sub.text.values['it-IT'] = '';
        question.subQuestions.push(sub);
    }

    removeSubQuestion(question: DDM.SurveyQuestion, idx: number) {
        question.subQuestions.splice(idx, 1);
    }

    save() {
        if (this.totalWeight != 100) {
            this.$opModal.show(InfoModal, {
                text: 'Validation Error',
                subtitle: 'Section weights must sum to 100. Current total: ' + this.totalWeight,
                deny: 'Close',
                denyCb: () => { this.$opModal.closeLast(); }
            });
            return;
        }

        this.saving = true;
        SurveyTemplateClient.saveDefault(this.template)
        .then(x => {
            this.template = x;
            this.ensureLocalizedStrings();
            this.saving = false;
            this.$opModal.show(InfoModal, {
                text: 'Success',
                subtitle: 'Template saved (v' + this.template.version + ')',
                deny: 'Close',
                denyCb: () => { this.$opModal.closeLast(); }
            });
        })
        .catch(() => {
            this.saving = false;
        });
    }
}
